import { valibotResolver } from "@hookform/resolvers/valibot";
import { Warning } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import { DialogTitle } from "common/components/ui/Dialog";
import { DrawerTitle } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from "common/components/ui/Select";
import SelectButton from "common/components/ui/SelectButton";
import Switch from "common/components/ui/Switch";
import useWorkspaceProxyMutations from "common/datahooks/useWorkspaceProxyMutations";
import { HTTPError } from "common/helpers/HTTP";
import { decodeProxyUrl } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { CustomProxySchema, type CustomProxySchemaType } from "common/schemas";
import type { CustomProxy } from "common/types";

const protocols = ["http", "https"];

interface ProxyFormProps {
  proxy?: CustomProxy;
  onAdded?: (proxyId: string) => void;
  onBack: () => void;
}
export default function CustomProxyForm({
  proxy = null,
  onAdded = null,
  onBack,
}: ProxyFormProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [hasAuthentication, setHasAuthentication] = useState(
    proxy ? proxy.url.includes("@") : false,
  );

  const {
    updateProxy,
    isUpdatingProxy,
    updateProxyError,
    createProxy,
    isCreatingProxy,
    createProxyError,
  } = useWorkspaceProxyMutations();

  let defaultValues: CustomProxySchemaType;
  if (proxy) {
    const { protocol, address, password, username } = decodeProxyUrl(proxy.url);
    defaultValues = {
      name: proxy.name,
      protocol,
      address,
      password,
      username,
    };
  } else {
    defaultValues = {
      name: "",
      protocol: "http",
      address: "",
      password: "",
      username: "",
    };
  }
  const {
    reset,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CustomProxySchemaType>({
    defaultValues,
    resolver: valibotResolver(CustomProxySchema),
  });

  function onSubmit(data: CustomProxySchemaType) {
    const { address, protocol, username, password, name } = data;
    const parsedData = {
      name,
      url: `${protocol}://${username}:${password}@${address}`,
    };
    if (proxy) {
      updateProxy({
        proxyId: proxy.id,
        updates: parsedData,
      }).then(() => onBack());
    } else {
      createProxy(parsedData).then(({ id }) => {
        if (onAdded) {
          onAdded(id);
        }
        onBack();
      });
    }
  }

  const proxyError = updateProxyError || createProxyError;

  // this is used when the user pastes a url in the input field, the protocol, address, username, password have to be parsed
  function parseUrl(url: string) {
    if (protocols.some((protocol) => url.startsWith(protocol))) {
      const [protocol, address] = url.split("://");
      if (address.includes("@")) {
        const [auth, actualAddress] = address.split("@");
        const [username, password] = auth.split(":");

        setValue("address", actualAddress);
        setValue("username", username);
        setValue("password", password);
        setHasAuthentication(true);
      } else {
        setValue("address", address);
        reset({ password: "", username: "" });
        setHasAuthentication(false);
      }
      setValue("protocol", protocol);
    }
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <>
      <Title className="mb-8">{proxy ? "Edit" : "Add"} custom proxy</Title>

      {/* Name */}
      <FormField
        label="Name"
        htmlFor="name"
        className="mb-4"
        error={errors.name?.message}
      >
        <Input id="name" placeholder="Enter name" {...register("name")} />
      </FormField>

      <FormField
        label="Proxy"
        htmlFor="address"
        error={errors.address?.message}
        className="mb-4"
      >
        <div className="flex gap-x-1.5">
          <Select
            value={watch("protocol")}
            onValueChange={(value) => {
              setValue("protocol", value);
            }}
          >
            <SelectTrigger className="min-w-[120px] lg:w-24" asChild>
              <SelectButton hasSelection={!!watch("protocol")}>
                <SelectValue />
              </SelectButton>
            </SelectTrigger>
            <SelectContent className="z-aboveDialog">
              {protocols.map((protocolString) => (
                <SelectItem value={protocolString} key={protocolString}>
                  <SelectItemText>{protocolString}://</SelectItemText>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            id="address"
            placeholder="Enter host:port"
            className="flex-1"
            {...register("address", {
              onChange: (e) => parseUrl(e.target.value.trim()),
            })}
          />
        </div>
      </FormField>

      <div className="flex items-center justify-between gap-4">
        <div>
          <h5 className="mb-2 text-button-sm text-black-700">Authentication</h5>
          <p className="text-black-700">
            If your proxy server requires authentication, enable this option and
            enter your username and password
          </p>
        </div>

        <Switch
          checked={hasAuthentication}
          onCheckedChange={(isChecked) => setHasAuthentication(isChecked)}
        />
      </div>

      {hasAuthentication && (
        <div className="my-4 flex gap-x-2">
          <FormField
            label="Username"
            htmlFor="username"
            className="flex-1"
            error={errors.username?.message}
          >
            <Input
              id="username"
              placeholder="Enter username"
              {...register("username")}
            />
          </FormField>
          <FormField
            label="Password"
            htmlFor="password"
            className="flex-1"
            error={errors.password?.message}
          >
            <Input
              id="password"
              placeholder="Enter password"
              {...register("password")}
            />
          </FormField>
        </div>
      )}
      {proxyError instanceof HTTPError && (
        <div className="flex items-center gap-0.5 text-xs text-yellow-500">
          <Warning weight="fill" size={16} />
          {proxyError.data === "ProxyCheckError"
            ? "Error validating proxy"
            : proxyError.serverMessage}
        </div>
      )}

      <div className="mt-8 flex justify-between gap-x-2.5">
        <Button
          className="lg:flex-1"
          variant="secondary-black"
          disabled={isUpdatingProxy || isCreatingProxy}
          onClick={() => {
            onBack();
            reset();
          }}
        >
          Cancel
        </Button>

        <Button
          className="lg:flex-1"
          isLoading={isUpdatingProxy || isCreatingProxy}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </div>
    </>
  );
}
